import {
  type EntitlementsInfo,
  EntitlementSources,
  PurchasesEntitlementInfo,
  entitlementsResolverSchema,
} from '@inkitt/contracts-common';
import { SocialGroupForPointsLiterals } from '@inkitt/titan/types/SocialGroupForPoints';
import { TransferResult } from '@inkitt/titan/types/UserTransfer';
import { z } from 'zod';
import { c } from '../../contract';
import { CoinsForSocialGroup } from '../../types/CoinsForSocialGroup';

const linkTypeSchema = z.object({
  linkType: z.enum(['199_offer']),
});

export const userRouter = c.router(
  {
    /** @deprecated Use getUserEmailSubscriptionStatus instead */
    getUserMarketingEmailSubscribed: {
      summary: '[Deprecated] Gets marketing email subscription status from Colt',
      method: 'GET',
      path: '/marketing-email/subscribed',
      responses: {
        200: z.object({
          subscribed: z.boolean(),
        }),
      },
    },
    getUserEmailSubscriptionStatus: {
      summary: 'Gets email subscription status from Iterable (marketing + subscriber channels)',
      method: 'GET',
      path: '/email-subscription/status',
      responses: {
        200: z.object({
          subscribed: z.boolean(),
        }),
      },
    },
    setUserEmailSubscriptionStatus: {
      summary: 'Subscribe or unsubscribe from Galatea email channels via Iterable',
      method: 'PATCH',
      path: '/email-subscription/status',
      body: z.object({
        subscribe: z.boolean(),
      }),
      responses: {
        204: c.noBody(),
      },
    },
    entitlements: {
      summary: 'Get user entitlements',
      method: 'GET',
      path: '/entitlements',
      query: entitlementsResolverSchema,
      responses: {
        // `hasUsedFreeTrial` was removed from `EntitlementsInfo` but added here for backwards compatibility
        200: c.type<EntitlementsInfo & { hasUsedFreeTrial?: boolean }>(),
      },
    },
    clearEntitlementsCache: {
      summary: 'Clear user entitlements cache',
      method: 'POST',
      path: '/entitlements/clear-cache',
      body: z
        .object({
          sources: z.array(z.enum(EntitlementSources)),
        })
        .optional(),
      responses: {
        200: z.object({
          success: z.boolean(),
        }),
      },
    },
    addSimulatedRevCatSubscription: {
      summary: 'Add a simulated RevCat subscription for a user',
      method: 'POST',
      path: '/simulated-revcat-subscription',
      body: z.object({
        entitlement: PurchasesEntitlementInfo,
      }),
      responses: {
        200: z.object({
          success: z.boolean(),
        }),
      },
    },
    clearSimulatedRevCatSubscription: {
      summary: 'Clear a simulated RevCat subscription for a user',
      method: 'DELETE',
      path: '/simulated-revcat-subscription',
      responses: {
        200: z.object({
          success: z.boolean(),
        }),
      },
    },
    useOneTimeToken: {
      summary: 'Validates if the token has already being used and/or saves the record of its use',
      method: 'POST',
      path: '/use-one-time-token',
      body: linkTypeSchema,
      responses: {
        200: z.object({
          success: z.boolean(),
          error: z.string().nullish(),
        }),
      },
    },
    transfer: {
      summary: 'Transfer user',
      method: 'POST',
      path: '/transfer',
      body: z.object({
        sourceUserId: z.string(),
        targetUserId: z.string(),
      }),
      responses: {
        200: z.object({
          result: TransferResult,
        }),
      },
    },

    addRecordForSocialGroupPoints: {
      summary: 'Add record for social group points',
      method: 'POST',
      path: '/social-group/add-record',
      body: z.object({
        socialGroup: z.enum(SocialGroupForPointsLiterals),
        points: z.number(),
      }),
      responses: {
        200: z.object({
          success: z.boolean(),
        }),
      },
    },
    getCoinsForSocialStatus: {
      summary: 'Get coins for social status',
      method: 'GET',
      path: '/coins-for-social-status',
      responses: {
        200: CoinsForSocialGroup,
      },
    },
    clearGroupPointsHistory: {
      summary: 'Clear group points history',
      method: 'DELETE',
      path: '/social-group/clear-history',
      responses: {
        200: z.object({
          success: z.boolean(),
        }),
      },
    },
    getSocialGroupRecord: {
      summary: 'Get social group points claim record for a device',
      method: 'GET',
      path: '/social-group/:socialGroup',
      pathParams: z.object({
        socialGroup: z.enum(SocialGroupForPointsLiterals),
      }),
      responses: {
        200: z
          .object({
            id: z.number(),
            userId: z.string(),
            deviceId: z.string(),
            socialGroup: z.enum(SocialGroupForPointsLiterals),
            points: z.number(),
            notificationShownAt: z.string().nullable(),
            createdAt: z.string(),
          })
          .nullable(),
      },
    },
    updateSocialGroupNotificationShown: {
      summary: 'Mark social group notification as shown',
      method: 'POST',
      path: '/social-group/notification-shown',
      body: z.object({
        socialGroup: z.enum(SocialGroupForPointsLiterals),
      }),
      responses: {
        200: z.object({
          success: z.boolean(),
        }),
      },
    },
    getOrGenerateShareToken: {
      summary: 'Get or generate a share token for a user',
      method: 'GET',
      path: '/share-token',
      responses: {
        200: z.object({
          shareToken: z.string(),
        }),
      },
    },
    saveMetadata: {
      summary: 'Ensure user metadata exists and firstInstalledOnDate is confirmed in Iterable',
      method: 'POST',
      path: '/save-metadata',
      body: c.noBody(),
      responses: {
        201: z.literal('Success'),
      },
    },
  },
  { pathPrefix: '/user' },
);
